import { useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl, Select } from '@/shared/components';
import { type ApiError, type ArrivalStepKind, type ArrivalStepResponse } from '@/shared/api';
import { GuestPhoneFrame } from '@/features/guest/preview/GuestPhoneFrame';
import { ArrivalPreviewImages } from './ArrivalPreviewImages';
import { arrivalProblem } from './arrivalProblem';
import type { ArrivalScope } from './useArrivalGuideApi';
import { useArrivalGuidePreview } from './useArrivalGuidePreview';

type Locale = 'de' | 'en';

interface Props {
  scope: ArrivalScope;
  guide: ArrivalStepKind;
  steps: ArrivalStepResponse[];
  /** Steht in der Vorschau vor den Schritten, etwa die eigenen Stellplätze beim Parken. */
  lead?: (locale: Locale) => ReactNode;
}

/** „So sieht es der Gast“: alle Schritte in der Sprache und für das Apartment der Wahl. */
export function ArrivalGuidePreview({ scope, guide, steps, lead }: Props) {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<Locale>('de');
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const results = useArrivalGuidePreview(scope, steps, propertyId, locale);
  const known = results.find((result) => result.data)?.data;
  const t = i18n.getFixedT(locale);
  const parking = guide === 'ParkingAlternatives';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.08em]">
          So sieht es der Gast
        </span>
        <SegmentedControl
          size="touch"
          aria-label="Sprache der Vorschau"
          value={locale}
          onChange={setLocale}
          options={[
            { value: 'de', label: 'DE' },
            { value: 'en', label: 'EN' },
          ]}
        />
      </div>
      {known && known.apartments.length > 1 && (
        <Select
          aria-label="Vorschau für Apartment"
          value={propertyId ?? known.propertyId}
          onChange={(event) => setPropertyId(event.target.value)}
        >
          {known.apartments.map((apartment) => (
            <option key={apartment.id} value={apartment.id}>
              Vorschau für {apartment.name}
            </option>
          ))}
        </Select>
      )}
      <GuestPhoneFrame label={`Handy-Vorschau ${parking ? 'Parken' : 'Anfahrt'}`} language={locale}>
        {lead?.(locale)}
        <div>
          <p className="text-accent font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
            {parking ? t('parking.alternativesTitle') : t('stations.stay.arrival.title')}
          </p>
          {parking && (
            <p className="text-fg-dim mt-1 text-xs">{t('parking.alternativesSubtitle')}</p>
          )}
        </div>
        {steps.length === 0 ? (
          <p className="text-fg-mute text-xs">
            Noch nichts hinterlegt – der Gast sieht diesen Teil nicht.
          </p>
        ) : (
          <ol className="space-y-4">
            {steps.map((step, index) => {
              const result = results[index];
              const rendered = result?.data;
              const failed = result?.isError
                ? arrivalProblem(result.error as unknown as ApiError)
                : null;
              return (
                <li key={step.id} className="flex gap-3">
                  {!parking && (
                    <span className="text-fg-mute mt-0.5 font-mono text-xs tabular-nums">
                      {index + 1}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-fg text-sm font-semibold">
                      {rendered?.title ?? step.titleDe}
                    </p>
                    {failed ? (
                      <p role="status" className="text-sealwax mt-1 text-xs">
                        Vorschau nicht verfügbar – {failed}
                      </p>
                    ) : (
                      <p className="text-fg mt-1 whitespace-pre-wrap text-[13px] leading-relaxed">
                        {rendered?.body ?? '…'}
                      </p>
                    )}
                    <ArrivalPreviewImages stepId={step.id} />
                    {rendered?.isTimeLocked && (
                      <p className="text-fg-mute mt-1 flex items-center gap-1.5 text-xs">
                        <Lock size={11} aria-hidden />
                        {t('stations.stay.arrival.lockedHintNoTime')}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </GuestPhoneFrame>
      <p className="text-ink-soft text-xs">
        {scope.kind === 'object' && !parking
          ? 'Die Fassung des Objekts, vom Server für das gewählte Apartment gerendert; ein Apartment mit eigener Fassung zeigt sie in seiner Anfahrhilfe.'
          : 'Vom Server gerendert wie im Gastportal.'}{' '}
        Codes werden in der Vorschau nie aufgelöst.
      </p>
    </div>
  );
}
